import { CaretRightIcon, ListMagnifyingGlassIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { Button } from '@/components/common/button';
import { Chip } from '@/components/common/chip';
import { Drawer } from '@/components/feedback/drawer';
import type { OrderDetail } from '@/features/orders';
import { REPLACEMENT_STATUS_CHIP, formatReplacementDate } from '@/features/requests';

import { DrawerSkeleton } from '../../components/drawer-skeleton';
import { unitLabel } from '../format';
import { useOrderReplacementRequests } from '../hooks';

import { ReplacementPreviewDrawer } from './replacement-preview-drawer';
import styles from './replacement-requests-drawer.module.scss';
import { RequestReplacementDrawer } from './request-replacement-drawer';

/** AC 112365 verbatim — copy only, no retry control. */
const LOAD_ERROR = "We couldn't load your replacement requests. Please try again.";
const EMPTY = 'No requests found.';

export type ReplacementRequestsDrawerProps = {
  order: OrderDetail;
  open: boolean;
  onClose: () => void;
};

/**
 * Every replacement request raised against one order. Reached from the Actions menu whenever the
 * order has history or the replacement window has elapsed; the Request Replacement pill appears iff
 * the backend reports the window open, and stacks the intake form on top of this drawer so closing
 * intake returns here rather than to Order Details (ADR-0022 pop-back).
 *
 * A plain `<ul>`, never `<DataTable>` — this is an unpaginated, unsortable, order-scoped list.
 */
export function ReplacementRequestsDrawer({
  order,
  open,
  onClose,
}: ReplacementRequestsDrawerProps) {
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const { data, isPending, isError } = useOrderReplacementRequests(order.id, open);

  return (
    <>
      <Drawer open={open} onClose={onClose} title="Replacement Requests" hideFooter>
        <div className={styles.body}>
          {isPending ? <DrawerSkeleton label="Loading replacement requests" /> : null}
          {isError ? <p className={styles.notice}>{LOAD_ERROR}</p> : null}
          {data && data.requests.length === 0 ? (
            <div className={styles.empty}>
              <ListMagnifyingGlassIcon size={24} className={styles.emptyIcon} />
              <p className={styles.emptyText}>{EMPTY}</p>
            </div>
          ) : null}
          {data && data.requests.length > 0 ? (
            <ul className={styles.list}>
              {data.requests.map((r) => (
                <li key={r.id} className={styles.item}>
                  <button type="button" className={styles.row} onClick={() => setPreviewId(r.id)}>
                    <span className={styles.text}>
                      <span className={styles.name}>
                        {unitLabel(r.itemName, r.quantity ?? 1, r.unitIndex)}
                      </span>
                      {/* `?? '—'`: a bundle line item carries no `productCategory` (ADO 119316),
                          the same degradation the warranty listing already renders. */}
                      <span className={styles.meta}>
                        {r.category ?? '—'} | {formatReplacementDate(r.requestedAt)}
                      </span>
                    </span>
                    <span className={styles.trailing}>
                      <Chip intent={REPLACEMENT_STATUS_CHIP[r.status].intent}>
                        {REPLACEMENT_STATUS_CHIP[r.status].label}
                      </Chip>
                      <CaretRightIcon size={16} className={styles.caret} />
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          {data?.isWithinWindow ? (
            <div className={styles.action}>
              <Button variant="neutral" size="large" onClick={() => setIntakeOpen(true)}>
                Request Replacement
              </Button>
            </div>
          ) : null}
        </div>
      </Drawer>
      <RequestReplacementDrawer
        orderId={order.id}
        customerId={order.customer.id}
        open={intakeOpen}
        onClose={() => setIntakeOpen(false)}
      />
      {/* Mounted only while a row is selected, so the id-gated detail query never fires with a
          blank requestId. Closing it pops back to this listing (ADR-0022). */}
      {previewId ? (
        <ReplacementPreviewDrawer
          orderId={order.id}
          requestId={previewId}
          open
          onClose={() => setPreviewId(null)}
        />
      ) : null}
    </>
  );
}
